import {
  EMBARGO_MESSAGE_KEYS,
  EMBARGO_MODES,
  EMBARGO_VALIDATION_TYPES,
  EMBARGO_VIOLATION_LEVELS,
} from '../constants';
import type { EmbargoRuleEvaluator, EmbargoViolation } from '../types';
import { isValueInCategory } from '../utils/listMatchers';

const isPickupManagementMode = (mode: string): boolean =>
  mode === EMBARGO_MODES.CREATE || mode === EMBARGO_MODES.EDIT;

/** Freezable freight restricted by destination (consignee) zip. */
export const freezableLaneByZipRule: EmbargoRuleEvaluator = {
  id: EMBARGO_MESSAGE_KEYS.FREEZABLE_LANE_BY_ZIP,
  level: EMBARGO_VIOLATION_LEVELS.SHIPMENT,
  evaluate: (context, config): EmbargoViolation[] => {
    if (!config.freezableLanesByZip.values.length) {
      return [];
    }

    const requiresFreezable = context.mode === EMBARGO_MODES.PROCESS;
    const validationType =
      context.mode === EMBARGO_MODES.PROCESS
        ? EMBARGO_VALIDATION_TYPES.ERROR
        : config.freezableLanesByZip.validationType;

    return context.shipments.reduce<EmbargoViolation[]>((acc, shipment) => {
      if (!shipment.destinationZip) {
        return acc;
      }
      if (requiresFreezable && !shipment.isFreezable) {
        return acc;
      }
      if (!isValueInCategory(shipment.destinationZip, config.freezableLanesByZip)) {
        return acc;
      }
      return [
        ...acc,
        {
          level: EMBARGO_VIOLATION_LEVELS.SHIPMENT,
          messageKey: EMBARGO_MESSAGE_KEYS.FREEZABLE_LANE_BY_ZIP,
          ruleId: EMBARGO_MESSAGE_KEYS.FREEZABLE_LANE_BY_ZIP,
          validationType,
          shipmentRowId: shipment.rowId,
        },
      ];
    }, []);
  },
};

/** Freezable freight restricted by destination terminal — create/edit/bulk/merge only. */
export const freezableLaneByTerminalRule: EmbargoRuleEvaluator = {
  id: EMBARGO_MESSAGE_KEYS.FREEZABLE_LANE_BY_TERMINAL,
  level: EMBARGO_VIOLATION_LEVELS.SHIPMENT,
  evaluate: (context, config): EmbargoViolation[] => {
    if (!isPickupManagementMode(context.mode) || !config.freezableLanesByTerminal.values.length) {
      return [];
    }

    return context.shipments.reduce<EmbargoViolation[]>((acc, shipment) => {
      if (!shipment.destinationTerminal) {
        return acc;
      }
      if (!isValueInCategory(shipment.destinationTerminal, config.freezableLanesByTerminal)) {
        return acc;
      }
      return [
        ...acc,
        {
          level: EMBARGO_VIOLATION_LEVELS.SHIPMENT,
          messageKey: EMBARGO_MESSAGE_KEYS.FREEZABLE_LANE_BY_TERMINAL,
          ruleId: EMBARGO_MESSAGE_KEYS.FREEZABLE_LANE_BY_TERMINAL,
          validationType: config.freezableLanesByTerminal.validationType,
          shipmentRowId: shipment.rowId,
        },
      ];
    }, []);
  },
};
